import { $generateHtmlFromNodes } from "@lexical/html";
import { $isLinkNode } from "@lexical/link";
import { $createTextNode, $getRoot, $insertNodes, $isElementNode, type LexicalNode } from "lexical";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import type { ChannelName } from "../../../../channels/types";
import { $isBroadcastNode } from "../../../../message/editor/nodes/broadcast-node";
import { $isChannelNode } from "../../../../message/editor/nodes/channel/channel-node";
import { $isVideoNode } from "../../../../message/editor/nodes/video-node";
import type { SerialisedLexicalEditorDesign } from "../../../../message/editor/types/serialised-editor-design";
import { generateNodesFromHtml } from "../../../../message/editor/utils/generate-nodes-from-html";
import { createConfiguredEditor } from "../../../../message/editor/utils/headless-editor";
import type { ChatChannelRecipient } from "../../../../message/editor/utils/use-chat-channel-recipients";

function unsupportedVideoNode(node: LexicalNode, slackVideoEnabled: boolean): boolean {
  // Cannot sent uploaded videos unless the Slack app has been updated
  return $isVideoNode(node) && !slackVideoEnabled;
}

export function convertLexicalDesign(
  from: NonNullable<ChannelName>,
  to: NonNullable<ChannelName>,
  design: SerialisedLexicalEditorDesign | null,
  slackVideoEnabled: boolean,
  slackChannelRecipients?: ChatChannelRecipient[],
): SerialisedLexicalEditorDesign {
  // This does not need the nodesSuperceder because only email can support tables.
  const oldEditor = createConfiguredEditor({ format: channelToFormat(from) });

  const state = oldEditor.parseEditorState(JSON.stringify(design));
  oldEditor.setEditorState(state);

  let htmlString = "";
  oldEditor.update(
    () => {
      // Remove nodes that are not supported in the target channel.
      const stripIllegalNodes = (node: LexicalNode): LexicalNode => {
        // link nodes are not directly supported in twilio, it's just text
        // however they are nested in paragraph nodes.
        if (to === "twilio" && $isLinkNode(node)) {
          return $createTextNode(node.getURL());
        }

        // slack references can be turned into plain text
        if (from === "slack" && $isBroadcastNode(node)) {
          return $createTextNode(node.getDisplay());
        }

        if (from === "slack" && $isChannelNode(node)) {
          if (!slackChannelRecipients) {
            return $createTextNode(`#${node.getChannelId()}`);
          }

          const channelName = slackChannelRecipients.find(
            ({ channel_id }) => node.getChannelId() === channel_id,
          )?.display;
          const display = channelName ? `#${channelName}` : "⚠️ Unknown Channel";
          return $createTextNode(display);
        }

        // loop through children of elements which can have children
        if ($isElementNode(node)) {
          const childNodes = node.getChildren();
          const preparedChildNodes = childNodes.map(stripIllegalNodes);
          childNodes.forEach((child) => child.remove());
          node.append(...preparedChildNodes);
        }
        return node;
      };
      const topLevelNodes = $getRoot().getChildren();

      const oldEditorNodes = topLevelNodes.map(stripIllegalNodes);
      oldEditorNodes.forEach((node) => node.remove());
      $getRoot().append(...oldEditorNodes);
      htmlString = $generateHtmlFromNodes(oldEditor, null);
    },
    { discrete: true },
  );

  const newEditor = createConfiguredEditor({ format: channelToFormat(to) });

  newEditor.update(
    () => {
      const nodes = generateNodesFromHtml(newEditor, htmlString, to);
      $insertNodes(
        to === "slack" ? nodes.filter((x) => !unsupportedVideoNode(x, slackVideoEnabled)) : nodes,
      );
      return true;
    },
    { discrete: true },
  );

  return newEditor.getEditorState().toJSON();
}
